/**
 * The email block registry (Email Studio handoff §1, §6). One list the builder, the preview and
 * the save payload all read — the server's twin is `Services/EmailBlocks.cs` + the controller's
 * `ParseSections`, which take the `server` names below.
 *
 * A block in the builder:
 *   text     { heading, body }
 *   imgtext  { side, img, alt, heading, body, link, url }
 *   photo    { img, alt, caption }
 *   band     { tone, ink, heading, body }
 *   event    { eventId, cta, url }
 *   button   { style, label, url }
 */

export const BLOCK_TYPES = {
  text: { type: 'text', server: 'Text', label: 'TEXT', mark: 'Tt' },
  imgtext: { type: 'imgtext', server: 'ImageText', label: 'IMAGE + TEXT', mark: 'IM', hasImage: true },
  photo: { type: 'photo', server: 'Photo', label: 'PHOTO', mark: 'PH', hasImage: true },
  band: { type: 'band', server: 'Band', label: 'HEADER BAND', mark: 'BD' },
  event: { type: 'event', server: 'Event', label: 'EVENT', mark: 'EV' },
  button: { type: 'button', server: 'Button', label: 'BUTTON', mark: 'BT' },
};

/** The ADD A BLOCK order. */
export const ADD_ORDER = ['text', 'imgtext', 'photo', 'band', 'event', 'button'];

let seq = 0;
export const newKey = () => `b${Date.now().toString(36)}${(seq += 1)}${Math.random().toString(36).slice(2, 5)}`;

/** A fresh block of `type`. `prev` is the current list: a new IMAGE + TEXT takes the side opposite the last one. */
export function emptyBlock(type, prev = []) {
  const key = newKey();
  switch (type) {
    case 'imgtext': {
      const last = [...prev].reverse().find((b) => b.type === 'imgtext');
      return { key, type, side: last && last.side === 'left' ? 'right' : 'left', img: '', alt: '', heading: '', body: '', link: '', url: '' };
    }
    case 'photo': return { key, type, img: '', alt: '', caption: '' };
    case 'band': return { key, type, tone: 'primary', ink: 'auto', heading: 'NEW SECTION', body: '' };
    case 'event': return { key, type, eventId: null, cta: 'GET TICKETS', url: '' };
    case 'button': return { key, type, style: 'filled', label: 'OPEN', url: '' };
    default: return { key, type: 'text', heading: '', body: '' };
  }
}

/** The four starters. Event blocks start on the page's next event when there is one. */
export const STARTERS = [
  { id: 'announce', name: 'ANNOUNCEMENT', note: 'One message, one button.' },
  { id: 'event', name: 'EVENT PROMO', note: 'Lead with an event card.' },
  { id: 'news', name: 'NEWSLETTER', note: 'Bands, stories, what is next.' },
  { id: 'blank', name: 'BLANK', note: 'Start from nothing.' },
];

export function buildStarter(id, events = []) {
  const ev = events[0]?.id || null;
  const ev2 = events[1]?.id || ev;
  const k = newKey;
  switch (id) {
    case 'announce':
      return [
        { key: k(), type: 'text', heading: 'Doors at eight, food at nine', body: 'Bring a chair and something to share.' },
        { key: k(), type: 'button', style: 'filled', label: 'RSVP', url: '' },
      ];
    case 'event':
      return [
        { key: k(), type: 'event', eventId: ev, cta: 'GET TICKETS', url: '' },
        { key: k(), type: 'imgtext', side: 'left', img: '', alt: '', heading: 'What to bring', body: 'Something for the table and a story for later.', link: '', url: '' },
      ];
    case 'news':
      return [
        { key: k(), type: 'band', tone: 'primary', ink: 'auto', heading: 'THIS MONTH', body: '' },
        { key: k(), type: 'imgtext', side: 'left', img: '', alt: '', heading: 'The first story', body: 'What happened, in a sentence or two.', link: 'READ MORE', url: '' },
        { key: k(), type: 'imgtext', side: 'right', img: '', alt: '', heading: 'The second story', body: 'One more thing worth knowing.', link: '', url: '' },
        { key: k(), type: 'band', tone: 'accent', ink: 'auto', heading: 'COMING UP', body: '' },
        { key: k(), type: 'event', eventId: ev2, cta: 'GET TICKETS', url: '' },
      ];
    default:
      return [];
  }
}

/** Builder block → the API's section shape. */
export function toSection(b) {
  const T = BLOCK_TYPES[b.type] || BLOCK_TYPES.text;
  const s = { type: T.server, heading: null, body: null, imageUrl: null, imageAlt: null, ctaUrl: null, ctaText: null };
  const v = (x) => (x == null || String(x).trim() === '' ? null : x);
  switch (b.type) {
    case 'text': return { ...s, heading: v(b.heading), body: v(b.body) };
    case 'imgtext': return { ...s, side: b.side || 'left', imageUrl: v(b.img), imageAlt: v(b.alt), heading: v(b.heading), body: v(b.body), ctaText: v(b.link), ctaUrl: v(b.url) };
    case 'photo': return { ...s, imageUrl: v(b.img), imageAlt: v(b.alt), caption: v(b.caption) };
    case 'band': return { ...s, tone: b.tone || 'primary', ink: b.ink || 'auto', heading: v(b.heading), body: v(b.body) };
    case 'event': return { ...s, eventId: b.eventId || null, ctaText: v(b.cta), ctaUrl: v(b.url) };
    case 'button': return { ...s, buttonStyle: b.style === 'outline' ? 'outline' : 'filled', ctaText: v(b.label), ctaUrl: v(b.url) };
    default: return s;
  }
}

/** API section → builder block. Reads the v1 names too (ImageLeft / ImageRight / Header). */
export function fromSection(s, i = 0) {
  const key = s.id || `s${i}`;
  const t = String(s.type || '').toLowerCase();
  const str = (x) => x || '';
  if (t === 'imagetext' || t === 'imageleft' || t === 'imageright') {
    return { key, type: 'imgtext', side: t === 'imageright' ? 'right' : t === 'imageleft' ? 'left' : (s.side === 'right' ? 'right' : 'left'), img: str(s.imageUrl), alt: str(s.imageAlt), heading: str(s.heading), body: str(s.body), link: str(s.ctaText), url: str(s.ctaUrl) };
  }
  if (t === 'band' || t === 'header') return { key, type: 'band', tone: s.tone || 'primary', ink: s.ink || 'auto', heading: str(s.heading), body: str(s.body) };
  if (t === 'photo') return { key, type: 'photo', img: str(s.imageUrl), alt: str(s.imageAlt), caption: str(s.caption) };
  if (t === 'event') return { key, type: 'event', eventId: s.eventId || null, cta: str(s.ctaText), url: str(s.ctaUrl) };
  if (t === 'button') return { key, type: 'button', style: s.buttonStyle === 'outline' ? 'outline' : 'filled', label: str(s.ctaText), url: str(s.ctaUrl) };
  return { key, type: 'text', heading: str(s.heading), body: str(s.body) };
}

/** A block with an image and no alt text — the NEEDS ALT flag and checklist row 4. */
export const needsAlt = (b) => !!BLOCK_TYPES[b.type]?.hasImage && !!b.img && !String(b.alt || '').trim();

/** The one-line excerpt on a block row. */
export function excerptOf(b, eventName) {
  if (b.type === 'event') return eventName || (b.eventId ? 'Event' : 'Pick an event');
  return b.heading || b.label || b.caption || b.body || 'Empty';
}

export const words = (s) => String(s || '').trim().split(/\s+/).filter(Boolean).length;
export const wordCount = (meta, blocks) => words(meta.intro) + blocks.reduce((n, b) => n + words(b.heading) + words(b.body) + words(b.caption), 0);

/**
 * The checklist while the draft is dirty — the server's `checks` mirrored client-side so the
 * rows move as you type (handoff §4 "04 Send": "the client may show it optimistically while
 * dirty"). Once saved, the server's list is the one drawn.
 */
export function optimisticChecks(meta, blocks) {
  const subject = String(meta.subject || '').trim();
  const preview = String(meta.preview || '').trim();
  return [
    { key: 'subject', label: 'Subject line', ok: !!subject, step: 1 },
    { key: 'preview', label: 'Preview text that says something new', ok: !!preview && preview.toLowerCase() !== subject.toLowerCase(), step: 1 },
    { key: 'blocks', label: 'At least one block', ok: blocks.length > 0, step: 1 },
    { key: 'alt', label: 'Alt text on every image', ok: !blocks.some(needsAlt), step: 1 },
    { key: 'test', label: 'Test sent since your last edit', ok: false, step: null },
  ];
}
